/**
 * tutorListAdapter — `formData.user.live_sessions[].tutor` → `UIParentTutorChoice[]`.
 *
 * API-aware (`LiveSessionLightResource.tutor` = `UserLighterResource`). Jamais importé par le DS.
 *
 * La liste des tuteurs d'un élève n'a **pas** d'endpoint côté parent : toutes les routes
 * `tutors` sont gardées `b_access`, et le rôle `parent` ne porte que `manage_own_children`.
 * On la dérive donc des séances régulières, exactement comme l'app élève
 * (`student/tutoring/MyCoachTabScreen.js` : map → filter → dédup par `id`). Cette source-là —
 * plutôt que les occurrences futures — couvre un tuteur dont l'élève n'a aucune séance à venir.
 *
 * **La matière n'est pas une propriété du tuteur** (`UITutor` n'en porte pas) : elle est
 * rapprochée par les séances, avec la même règle que les cartes de live — la matière du siège
 * EST celle de l'objectif pédagogique (`subjectsForSession`, `liveSessionListAdapter`).
 */
import {
    subjectsForSession,
    toUITutor,
    type ApiLiveSessionNested,
    type ApiStudentForSubjects,
} from "../LiveSessions/liveSessionListAdapter"
import { type ApiSchoolSubject } from "../shared/schoolSubjectAdapter"
import { type UIParentTutorChoice } from "../../../ui/types"

export type ToUIParentTutorChoicesOptions = {
    /** `formData.user` — porte `pedagogical_objectives`, d'où sortent les matières du siège. */
    student?: ApiStudentForSubjects | null
    /** `staticData.school_subjects`. */
    apiSubjects?: ApiSchoolSubject[]
}

/**
 * Les tuteurs de l'élève, dédupliqués par id, dans l'ordre où ses séances les rencontrent.
 *
 * Un tuteur qui enseigne deux séances n'apparaît qu'une fois et cumule leurs matières
 * (« SVT · Physique ») ; un tuteur sans matière résolue n'a pas de sous-titre plutôt qu'un
 * libellé vide.
 */
export function toUIParentTutorChoices(
    liveSessions: ApiLiveSessionNested[] | null | undefined,
    options: ToUIParentTutorChoicesOptions = {}
): UIParentTutorChoice[] {
    const { student, apiSubjects = [] } = options

    const byTutorId = new Map<string, { choice: UIParentTutorChoice; subjectNames: string[] }>()

    for (const liveSession of liveSessions ?? []) {
        const rawTutor = liveSession?.tutor
        if (rawTutor?.id == null) continue

        const key = String(rawTutor.id)
        const entry = byTutorId.get(key) ?? {
            choice: { tutor: toUITutor(rawTutor) },
            subjectNames: [],
        }

        for (const subject of subjectsForSession(liveSession?.id, student, apiSubjects, liveSession?.school_subjects)) {
            if (subject.name && !entry.subjectNames.includes(subject.name)) entry.subjectNames.push(subject.name)
        }

        byTutorId.set(key, entry)
    }

    return [...byTutorId.values()].map(({ choice, subjectNames }) => ({
        ...choice,
        subjectsLabel: subjectNames.length > 0 ? subjectNames.join(" · ") : undefined,
    }))
}
